import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import StorageIcon from '@mui/icons-material/Storage';
import FeedbackIcon from '@mui/icons-material/Feedback';
import EventIcon from '@mui/icons-material/Event';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import RoleGuard from '../auth/RoleGuard';
import { featureFlags } from '../../utils/featureFlags';
import NotificationBell from '../common/NotificationBell';

const MainLayout: React.FC = () => {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [menuOverflows, setMenuOverflows] = useState(false);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLDivElement>(null);
  const userMenuAreaRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout, isAdmin, isSuperadmin } = useAuth();

  const menuItems = [
    { text: t('dashboard.title'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('case.menuTitle'), icon: <GavelIcon />, path: '/cases/create' },
    { text: t('client.title'), icon: <PeopleIcon />, path: '/clients' },
    { text: t('accounting.title'), icon: <AccountBalanceIcon />, path: '/accounting' },
    { text: t('plazos.title'), icon: <EventIcon />, path: '/plazos' },
  ];

  const adminMenuItems = [
    ...(featureFlags.isFeatureEnabled('providers', user?.role || 'regular')
      ? [{ text: t('providers.title'), icon: <BusinessIcon />, path: '/providers/fixed-costs' }]
      : []),
    { text: 'Suscripción', icon: <SettingsIcon />, path: '/admin/subscription' },
  ];

  const superadminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/superadmin' },
    { text: 'Administradores', icon: <AdminPanelSettingsIcon />, path: '/superadmin/admins' },
    { text: 'Organizaciones', icon: <BusinessIcon />, path: '/superadmin/organizations' },
    { text: 'Cuentas de Prueba', icon: <PeopleIcon />, path: '/superadmin/tester-accounts' },
    { text: 'Monitoreo', icon: <SettingsIcon />, path: '/superadmin/monitoring' },
    { text: 'Planes', icon: <SettingsIcon />, path: '/superadmin/plans' },
    { text: t('auditLogs.title'), icon: <HistoryIcon />, path: '/superadmin/audit-logs' },
    { text: t('dataRetention.title'), icon: <StorageIcon />, path: '/superadmin/data-retention' },
    { text: t('securityDashboard.title'), icon: <SecurityIcon />, path: '/superadmin/security-dashboard' },
    { text: t('admin.testerFeedback'), icon: <FeedbackIcon />, path: '/superadmin/tester-feedback' },
    { text: t('admin.featureWaitlist'), icon: <NotificationsNoneIcon />, path: '/superadmin/feature-waitlist' },
  ];

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleUserMenuClose();
  };

  const getRoleLabel = () => {
    if (!user) return '';
    switch (user.role) {
      case 'superadmin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      default:
        return 'Usuario';
    }
  };

  const getRoleColor = () => {
    if (!user) return 'default';
    switch (user.role) {
      case 'superadmin':
        return 'error';
      case 'admin':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Check if menu items overflow and need hamburger menu
  useEffect(() => {
    const checkMenuOverflow = () => {
      // On small screens, always show hamburger
      if (isSmallScreen) {
        setShowHamburgerMenu(true);
        return;
      }

      // On larger screens, check if buttons actually overflow
      if (menuContainerRef.current && menuItemsRef.current) {
        // Get all visible button elements
        const buttons = menuItemsRef.current.querySelectorAll('button');
        if (buttons.length === 0) {
          setShowHamburgerMenu(false);
          return;
        }

        // Get the last button
        const lastButton = buttons[buttons.length - 1] as HTMLElement;
        const lastButtonRect = lastButton.getBoundingClientRect();
        
        // Calculate available right edge - where the last button can go
        let availableRightEdge: number;
        
        if (userMenuAreaRef.current) {
          // Use actual user menu area position
          const userMenuRect = userMenuAreaRef.current.getBoundingClientRect();
          availableRightEdge = userMenuRect.left - 8; // 8px gap between menu and user area
        } else {
          // If user menu area not rendered yet, use window width minus estimated space
          // Estimate: ~120px for user menu (chip + icon + padding) + 8px gap
          availableRightEdge = window.innerWidth - 128;
        }
        
        // Check if last button's right edge would overflow past the available space
        // Add a small buffer (12px) to ensure button is fully visible before switching
        const overflows = lastButtonRect.right + 12 > availableRightEdge;
        setMenuOverflows(overflows);
        
        // Show hamburger only if last button would be cut off
        // On large screens, always show menu in header unless buttons overflow
        setShowHamburgerMenu(overflows);
      } else {
        // If refs not ready, default to showing menu (not hamburger) on large screens
        setShowHamburgerMenu(false);
      }
    };

    // Check on mount and when window resizes
    checkMenuOverflow();
    window.addEventListener('resize', checkMenuOverflow);
    
    // Also check when location changes (different pages may have different menu items)
    // Use a longer timeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkMenuOverflow, 200);

    return () => {
      window.removeEventListener('resize', checkMenuOverflow);
      clearTimeout(timeoutId);
    };
  }, [location.pathname, user, isSmallScreen]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#ffffff' }}>
      <AppBar 
        position="fixed"
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)',
          zIndex: 1200,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 }, minHeight: '64px !important' }}>
          <Box
            ref={logoRef}
            component="div"
            onClick={() => navigate(isSuperadmin() ? '/superadmin' : '/dashboard')}
            sx={{ 
              flexGrow: 0, 
              flexShrink: 0,
              mr: { xs: 2, md: 4 },
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                fontSize: '1.125rem',
                letterSpacing: '-0.01em',
                color: '#000000',
              }}
            >
              Ston
            </Typography>
          </Box>
          {showHamburgerMenu ? (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {user && (
                  <Chip
                    label={getRoleLabel()}
                    color={getRoleColor()}
                    size="small"
                    sx={{ fontWeight: 500, display: { xs: 'none', sm: 'flex' } }}
                  />
                )}
                <IconButton
                  color="inherit"
                  aria-label="open menu"
                  edge="end"
                  onClick={handleMobileMenuOpen}
                  sx={{
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: '#ffffff',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    mt: 1,
                    minWidth: 240,
                    maxWidth: 320,
                    maxHeight: 'calc(100vh - 80px)',
                    overflow: 'auto',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <RoleGuard allowedRoles={['regular', 'admin']}>
                  {menuItems.map((item) => {
                    const isActive = location.pathname === item.path || 
                      (item.path === '/cases/create' && location.pathname.startsWith('/cases/'));
                    return (
                      <MenuItem
                        key={item.path}
                        selected={isActive}
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.12)',
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>
                            {item.icon}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: isActive ? 500 : 400 }}>
                            {item.text}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </RoleGuard>
                <RoleGuard allowedRoles={['admin']}>
                  <Divider sx={{ my: 1 }} />
                  {adminMenuItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      selected={location.pathname === item.path}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>
                          {item.icon}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: location.pathname === item.path ? 500 : 400 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </RoleGuard>
                <RoleGuard allowedRoles={['superadmin']}>
                  <Divider sx={{ my: 1 }} />
                  {superadminMenuItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      selected={location.pathname === item.path}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>
                          {item.icon}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: location.pathname === item.path ? 500 : 400 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </RoleGuard>
                <Divider sx={{ my: 1 }} />
                {user && (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <AccountCircleIcon sx={{ color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                )}
                <MenuItem 
                  onClick={() => {
                    handleLogout();
                    handleMobileMenuClose();
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LogoutIcon sx={{ fontSize: 20 }} />
                    <Typography variant="body2">Cerrar Sesión</Typography>
                  </Box>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Box 
                ref={menuContainerRef}
                sx={{ 
                  display: 'flex', 
                  gap: 0.5, 
                  flexGrow: 1,
                  overflow: 'hidden',
                  minWidth: 0,
                }}
              >
                <Box
                  ref={menuItemsRef}
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    flexGrow: 1,
                    minWidth: 0,
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  <RoleGuard allowedRoles={['regular', 'admin']}>
                    {menuItems.map((item) => {
                      const isActive = location.pathname === item.path || 
                        (item.path === '/cases/create' && location.pathname.startsWith('/cases/'));
                      return (
                        <Button
                          key={item.path}
                          color="inherit"
                          startIcon={item.icon}
                          onClick={() => handleNavigation(item.path)}
                          sx={{
                            backgroundColor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                            color: isActive ? '#000000' : '#666666',
                            fontWeight: isActive ? 500 : 400,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            '&:hover': {
                              backgroundColor: isActive ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                              color: '#000000',
                            },
                            transition: 'all 0.2s ease-in-out',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                          }}
                        >
                          {item.text}
                        </Button>
                      );
                    })}
                  </RoleGuard>
                  <RoleGuard allowedRoles={['admin']}>
                    {adminMenuItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <Button
                          key={item.path}
                          color="inherit"
                          startIcon={item.icon}
                          onClick={() => handleNavigation(item.path)}
                          sx={{
                            backgroundColor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                            color: isActive ? '#000000' : '#666666',
                            fontWeight: isActive ? 500 : 400,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            '&:hover': {
                              backgroundColor: isActive ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                              color: '#000000',
                            },
                            transition: 'all 0.2s ease-in-out',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                          }}
                        >
                          {item.text}
                        </Button>
                      );
                    })}
                  </RoleGuard>
                  <RoleGuard allowedRoles={['superadmin']}>
                    {superadminMenuItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <Button
                          key={item.path}
                          color="inherit"
                          startIcon={item.icon}
                          onClick={() => handleNavigation(item.path)}
                          sx={{
                            backgroundColor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                            color: isActive ? '#000000' : '#666666',
                            fontWeight: isActive ? 500 : 400,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            '&:hover': {
                              backgroundColor: isActive ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                              color: '#000000',
                            },
                            transition: 'all 0.2s ease-in-out',
                            '& .MuiButton-startIcon': {
                              marginRight: 1,
                            },
                          }}
                        >
                          {item.text}
                        </Button>
                      );
                    })}
                  </RoleGuard>
                </Box>
              </Box>
              <Box 
                ref={userMenuAreaRef}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}
              >
                {user && (
                  <Chip
                    label={getRoleLabel()}
                    color={getRoleColor()}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                )}
                <NotificationBell />
                <IconButton
                  onClick={handleUserMenuOpen}
                  sx={{
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <AccountCircleIcon />
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {user && (
                    <MenuItem disabled>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem onClick={() => { navigate('/settings/security'); handleUserMenuClose(); }}>
                    <SecurityIcon sx={{ mr: 1, fontSize: 20 }} />
                    Seguridad
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/settings/privacy'); handleUserMenuClose(); }}>
                    <PrivacyTipIcon sx={{ mr: 1, fontSize: 20 }} />
                    Privacidad y Datos
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                    Cerrar Sesión
                  </MenuItem>
                </Menu>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 2, md: 4 },
          px: { xs: 2, md: 4 },
          pb: user?.isTestAccount ? { xs: '96px', sm: '112px' } : { xs: 2, md: 4 },
          width: '100%',
          mt: '64px',
          backgroundColor: '#ffffff',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;

